import { Box, Button, ContextualBanner, Modal } from "@bcc-ui/ui-components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    GradingWorkflowSelection,
    isTemplateWorkflow,
} from "components/asset-management-system/custom-views/tabs/value/steps/smart-grading-step/GradingWorkflowSelection";
import { WorkflowForGrading } from "services/asset-management/AssetGradingService";

interface Props {
    isOpen: boolean;
    isLaunching: boolean;
    assetsToRegradeCount: number;
    workflows: WorkflowForGrading[];
    templates: WorkflowForGrading[];
    initialWorkflowUuid?: string;
    onClose: () => void;
    onConfirm: (workflowUuid: string, templateSelected: boolean) => void;
}

export const RerunGradingDialog = (props: Props) => {
    const { t } = useTranslation();
    const [workflowUuid, setWorkflowUuid] = useState<string>();
    const [templateSelected, setTemplateSelected] = useState<boolean>(false);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        if (props.isOpen) {
            setWorkflowUuid(props.initialWorkflowUuid);
            setTemplateSelected(isTemplateWorkflow(props.initialWorkflowUuid, props.templates));
        }
    }, [props.isOpen, props.initialWorkflowUuid, props.templates]);

    const handleConfirm = () => {
        if (!workflowUuid) {
            setShowError(true);
            return;
        }
        props.onConfirm(workflowUuid, templateSelected);
    };

    const handleClose = () => {
        setWorkflowUuid(undefined);
        setTemplateSelected(false);
        setShowError(false);
        props.onClose();
    };

    const isConfirmDisabled = (showError && !workflowUuid) || props.isLaunching;

    return (
        <Modal
            title={t("AssetManagement.value.smartGrading.rerun.title")}
            isOpen={props.isOpen}
            hideModal={handleClose}
            buttons={
                <>
                    <Button
                        variant="Primary"
                        disabled={isConfirmDisabled}
                        isLoading={props.isLaunching}
                        onClick={handleConfirm}
                    >
                        {t("AssetManagement.value.smartGrading.rerun.title")}
                    </Button>
                    <Button variant="Secondary" disabled={props.isLaunching} onClick={handleClose}>
                        {t("Common.cancel")}
                    </Button>
                </>
            }
        >
            <ContextualBanner variant={"Warning"}>
                {t("AssetManagement.value.smartGrading.rerun.warning", {
                    count: props.assetsToRegradeCount,
                })}
            </ContextualBanner>
            <Box columnLayout gap="Sm">
                <GradingWorkflowSelection
                    key={`${props.isOpen}-${props.initialWorkflowUuid ?? ""}`}
                    setWorkflowUuid={setWorkflowUuid}
                    setTemplateSelected={setTemplateSelected}
                    workflows={props.workflows}
                    templates={props.templates}
                    workflowUuid={workflowUuid}
                    hasMissingWorkflowError={showError}
                    isRerunning
                />
            </Box>
        </Modal>
    );
};
